import React from 'react';

interface FeaturedEventCardProps {
  event: {
    id: string;
    title: string;
    image?: string;
    date: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    timezone?: string;
    organizer?: string;
    venue?: string;
    venueAddress?: {
      addressLocality?: string;
      addressRegion?: string;
    };
    url: string;
    isFree?: boolean;
  };
}

const FeaturedEventCard: React.FC<FeaturedEventCardProps> = ({ event }) => {
  // Format date and time, with "TBD" fallback for unparseable dates
  const formatDateTime = () => {
    const startDate = new Date(event.date);
    if (!event.date || isNaN(startDate.getTime())) return 'TBD';

    const endDate = event.endDate ? new Date(event.endDate) : null;

    const dayOfWeek = startDate.toLocaleDateString('en-US', { weekday: 'short' });
    const month = startDate.toLocaleDateString('en-US', { month: 'short' });
    const day = startDate.getDate();

    // Extract time from ISO string (v4 start.utc includes time, e.g. "2026-03-03T04:00:00Z")
    let timeStr = '';
    if (event.startTime) {
      const time = new Date(`${event.date}T${event.startTime}`);
      if (!isNaN(time.getTime())) {
        timeStr = time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      }
    } else if (event.date.includes('T')) {
      timeStr = startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }

    if (endDate && !isNaN(endDate.getTime()) && endDate.getTime() !== startDate.getTime()) {
      const endDayOfWeek = endDate.toLocaleDateString('en-US', { weekday: 'short' });
      const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
      const endDay = endDate.getDate();

      return `${dayOfWeek}, ${month} ${day} - ${endDayOfWeek}, ${endMonth} ${endDay}${timeStr ? ` ${timeStr}` : ''} ${event.timezone || 'JST'}`;
    }

    return `${dayOfWeek}, ${month} ${day}${timeStr ? ` - ${timeStr}` : ''} ${event.timezone || 'JST'}`;
  };

  // Format location
  const formatLocation = () => {
    if (event.venueAddress?.addressLocality && event.venueAddress?.addressRegion) {
      return `${event.venueAddress.addressLocality}, ${event.venueAddress.addressRegion}`;
    }
    if (event.venue) {
      return event.venue;
    }
    return '';
  };

  return (
    <a 
      href={event.url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="block w-full h-full"
    >
      <article className="flex flex-col w-full h-full rounded-lg overflow-hidden border border-gray-200 bg-white hover:shadow-lg transition-shadow">
        {/* Event Image */}
        {event.image && (
          <div className="w-full aspect-video overflow-hidden bg-gray-100">
            <img 
              src={event.image} 
              alt={event.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {/* Event Details */}
        <div className="flex flex-col p-4 gap-3 flex-grow">
          {/* Date and Time */}
          <div className="text-sm text-gray-600 font-medium">
            {formatDateTime()}
          </div>
          
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
            {event.title}
          </h3>
          
          {/* Organizer */}
          {event.organizer && (
            <div className="text-sm text-gray-600">
              by {event.organizer}
            </div>
          )}
          
          {/* Location */}
          {formatLocation() && (
            <div className="text-sm text-gray-600">
              {formatLocation()}
            </div>
          )}
        </div>
      </article>
    </a>
  );
};

export default FeaturedEventCard;
